/** Typed client for the platform's own API (Cloudflare Pages Functions). */

async function getJson<T>(path: string): Promise<T> {
  const res = await fetch(path, { headers: { Accept: "application/json" } });
  if (!res.ok) {
    throw new Error(`${path} → HTTP ${res.status}`);
  }
  return (await res.json()) as T;
}

async function postJson<T>(path: string, body: unknown): Promise<T> {
  const res = await fetch(path, {
    method: "POST",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok && res.status !== 404) {
    throw new Error(`${path} → HTTP ${res.status}`);
  }
  return (await res.json()) as T;
}

export interface HealthResponse {
  status: string;
  service: string;
  phase: string;
  environment: string;
  time: string;
  database: {
    name: string | null;
    bound: boolean;
    connected: boolean;
    latencyMs: number | null;
    error?: string;
  };
  checks: {
    database_bound: boolean;
    database_connected: boolean;
    shopify_domain_set: boolean;
    shopify_token_set: boolean;
    shopify_api_version: string | null;
  };
}

export interface TableCount {
  name: string;
  rows: number;
}

export interface ShopifyStatusResponse {
  configured: boolean;
  storeDomain: string | null;
  apiVersion: string | null;
  tokenPresent: boolean;
}

export interface ShopifyConnectionResponse {
  configured: boolean;
  connected?: boolean;
  shopName?: string | null;
  shopDomain?: string | null;
  apiVersion?: string | null;
  error?: string;
}

export interface ShopifySchemaResponse {
  configured: boolean;
  error?: string;
  customerMetafields?: {
    expected: string[];
    found: Array<{ key: string; name: string; namespace: string; type: string }>;
    missing: string[];
  };
  metaobjects?: {
    expected: string[];
    found: Array<{ type: string; name: string; fieldKeys: string[] }>;
    missing: string[];
  };
}

export interface SchemaValidateResponse {
  configured: boolean;
  error?: string;
  summary?: {
    uuidFields: string;
    imageFields: string;
    jsonFields: string;
    metaobjectReferences: string;
    overall: string;
  };
  failures?: Array<{
    metaobjectType: string;
    key: string;
    expected: string | null;
    live: string | null;
    status: string;
  }>;
  imageFieldsIgnored?: string[];
}

export interface SandlotCustomerSummary {
  id: string;
  email: string | null;
  name: string | null;
  phone: string | null;
  memberSince: string | null;
}

export interface MatchAssessment {
  shopifyCustomerId: string | null;
  confidenceScore: number;
  matchStatus: string;
  reasons: string[];
  conflicts: string[];
  candidateCount: number;
}

export interface SandlotCustomerDetail {
  found: boolean;
  searched?: { id: string | null; email: string | null };
  note?: string;
  sandlot?: {
    id: string;
    name: string | null;
    email: string | null;
    phone: string | null;
    memberSince: string | null;
    addressShort: string | null;
    isLeader: boolean;
    clubs: Array<{
      groupId: string | null;
      officialName: string | null;
      officialCity: string | null;
      officialRegion: string | null;
      isLeader: number | null;
      status: string | null;
    }>;
    totalGroupMemberships?: number;
    eventsNote: string;
  };
  shopify: {
    configured: boolean;
    candidates?: Array<{ id: string; email: string | null; phone: string | null }>;
    metafields?: Array<{ namespace: string; key: string; type: string; value: string | null }> | null;
    error?: string;
  };
  match?: MatchAssessment;
  proposedPayload?: {
    namespace: string;
    fields: Record<string, unknown>;
    notes: string[];
  };
}

export interface MigrationStatsResponse {
  totals: { jobs: number; matches: number; errors: number };
  matchesByStatus: Array<{ status: string; n: number }>;
}

export interface MigrationJob {
  id: number;
  status: string;
  jobType: string | null;
  startedAt: string | null;
  completedAt: string | null;
  totalRecords: number;
  processedRecords: number;
  successfulRecords: number;
  failedRecords: number;
  createdAt: string;
  updatedAt: string;
}

export interface CustomerMatch {
  id: number;
  sandlotCustomerId: string;
  shopifyCustomerId: string | null;
  confidenceScore: number;
  candidateCount: number;
  matchStatus: string;
  matchReasons: string | null;
  conflicts: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface PreviewSummary {
  source: { clubs: number; events: number; workouts: number };
  previewsStored: number;
}

export interface MetaobjectInput {
  type: string;
  handle: string;
  fields: Array<{ key: string; value: string }>;
}

export interface GeneratePreviewsResult {
  ok: boolean;
  error?: string;
  entity?: string;
  jobId?: number;
  generated?: number;
  errors?: number;
  withWarnings?: number;
  sample?: {
    entityType: string;
    metaobjectType: string;
    sandlotId: string;
    handle: string;
    input: MetaobjectInput;
    warnings: string[];
  } | null;
}

export interface StoredPreview {
  id: number;
  entityType: string;
  sandlotId: string;
  shopifyHandle: string;
  payloadJson: string;
  status: string;
  createdAt: string;
}

export const api = {
  health: () => getJson<HealthResponse>("/api/health"),
  shopifyStatus: () => getJson<ShopifyStatusResponse>("/api/shopify/status"),
  shopifyConnection: () =>
    getJson<ShopifyConnectionResponse>("/api/shopify/connection"),
  shopifySchema: () => getJson<ShopifySchemaResponse>("/api/shopify/schema"),
  schemaValidate: () => getJson<SchemaValidateResponse>("/api/shopify/schema/validate"),
  sandlotSearch: (q: string) =>
    getJson<{ customers: SandlotCustomerSummary[] }>(
      `/api/sandlot/customers?search=${encodeURIComponent(q)}`,
    ),
  sandlotCustomer: (params: { id?: string; email?: string }) => {
    const qs = new URLSearchParams();
    if (params.id) qs.set("id", params.id);
    if (params.email) qs.set("email", params.email);
    return getJson<SandlotCustomerDetail>(`/api/sandlot/customer?${qs.toString()}`);
  },
  evaluateMatch: (params: { id?: string; email?: string }) =>
    postJson<{ ok: boolean; error?: string; match?: MatchAssessment }>(
      "/api/matching/evaluate",
      params,
    ),
  previewsSummary: () =>
    getJson<PreviewSummary>("/api/migration/previews/summary"),
  generatePreviews: (params: { entity_type: string; limit: number }) =>
    postJson<GeneratePreviewsResult>("/api/migration/previews/generate", params),
  listPreviews: (entityType?: string, limit = 25) =>
    getJson<{ previews: StoredPreview[] }>(
      `/api/migration/previews?limit=${limit}${entityType ? `&entity_type=${entityType}` : ""}`,
    ),
  writesStatus: () =>
    getJson<{ writesEnabled: boolean; note: string }>("/api/migration/writes/status"),
  buildClubPreview: (sandlotId: string) =>
    getJson<{ ok: boolean; error?: string; preview?: GeneratePreviewsResult["sample"] }>(
      `/api/migration/previews/build?entity_type=club&sandlot_id=${encodeURIComponent(sandlotId)}`,
    ),
  requestApproval: (sandlotId: string, approvedBy = "admin") =>
    postJson<{ ok: boolean; error?: string; status?: string }>("/api/migration/approvals", {
      entity_type: "club",
      entity_id: sandlotId,
      status: "approved",
      approved_by: approvedBy,
    }),
  executeClubWrite: (sandlotId: string) =>
    postJson<{
      ok: boolean;
      error?: string;
      status?: string;
      duplicate?: boolean;
      shopifyGid?: string;
      handle?: string;
      type?: string;
    }>("/api/migration/writes/metaobject", { entity_type: "club", sandlot_id: sandlotId }),
  metaobjectReadiness: (entity: string) =>
    getJson<{
      entity: string;
      total: number;
      alreadyMigrated: number;
      remaining: number;
      validationPassed: number;
      blocked: number;
      warnings: number;
      batchApproved: boolean;
      writesEnabled: boolean;
    }>(`/api/migration/metaobjects/readiness?entity_type=${entity}`),
  approveEntityBatch: (entity: string, approvedBy = "admin") => {
    const type = entity === "club" ? "ruck_club_batch" : `${entity}_batch`;
    return postJson<{ ok: boolean; status?: string }>("/api/migration/approvals", {
      entity_type: type,
      entity_id: "all",
      status: "approved",
      approved_by: approvedBy,
    });
  },
  runEntityBatch: (entity: string, batchSize = 25, after = "") =>
    postJson<{
      ok: boolean;
      error?: string;
      entity?: string;
      completed?: number;
      skipped?: number;
      failed?: number;
      batchProcessed?: number;
      total?: number;
      totalMigrated?: number;
      remaining?: number;
      nextCursor?: string;
      done?: boolean;
      failures?: Array<{ sandlotId: string; error: string }>;
    }>("/api/migration/writes/metaobjects/batch", { entity_type: entity, batch_size: batchSize, after }),
  clubsReadiness: () =>
    getJson<{
      totalClubs: number;
      alreadyMigrated: number;
      remaining: number;
      validationPassed: number;
      blocked: number;
      warnings: number;
      batchApproved: boolean;
      writesEnabled: boolean;
    }>("/api/migration/clubs/readiness"),
  approveClubBatch: (approvedBy = "admin") =>
    postJson<{ ok: boolean; status?: string }>("/api/migration/approvals", {
      entity_type: "ruck_club_batch",
      entity_id: "all",
      status: "approved",
      approved_by: approvedBy,
    }),
  runClubBatch: (batchSize = 25) =>
    postJson<{
      ok: boolean;
      error?: string;
      completed?: number;
      skipped?: number;
      failed?: number;
      batchProcessed?: number;
      totalClubs?: number;
      totalMigrated?: number;
      remaining?: number;
      done?: boolean;
      failures?: Array<{ sandlotId: string; error: string }>;
    }>("/api/migration/writes/clubs/batch", { batch_size: batchSize }),
  rollbackPreview: (sandlotId: string) =>
    getJson<{
      ok: boolean;
      error?: string;
      note?: string;
      toDelete?: { handle: string | null; shopifyGid: string };
      plan?: { mutation: string; variables: Record<string, unknown> };
    }>(`/api/migration/writes/rollback-preview?sandlot_id=${encodeURIComponent(sandlotId)}`),
  stats: () => getJson<MigrationStatsResponse>("/api/migration/stats"),
  tables: () => getJson<{ tables: TableCount[] }>("/api/migration/tables"),
  jobs: () => getJson<{ jobs: MigrationJob[] }>("/api/migration/jobs"),
  matches: () => getJson<{ matches: CustomerMatch[] }>("/api/migration/matches"),
};
